import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import { listOrders } from '../../services/liveStamping';
import { formatDateTimeIST } from '../../utils/dateUtils';

const STATUS_COLORS = {
  placed: 'bg-gray-100 text-gray-700',
  executing: 'bg-blue-100 text-blue-700',
  preview_submitted: 'bg-yellow-100 text-yellow-800',
  metadata_ready: 'bg-indigo-100 text-indigo-700',
  compliance_check: 'bg-purple-100 text-purple-700',
  pending_verification: 'bg-purple-100 text-purple-700',
  ops_review: 'bg-orange-100 text-orange-700',
  completed: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
};

const FILTER_OPTIONS = [
  { label: 'Pending Review', value: 'preview_submitted,metadata_ready,compliance_check,pending_verification,ops_review' },
  { label: 'All Orders', value: '' },
  { label: 'Completed', value: 'completed' },
  { label: 'Rejected', value: 'rejected' },
];

const PAGE_SIZE = 20;

const AdminOrderQueue = forwardRef(({ onSelectOrder }, ref) => {
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState(FILTER_OPTIONS[0].value);
  const [page, setPage] = useState(0);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const filters = { limit: PAGE_SIZE, offset: page * PAGE_SIZE };
      if (statusFilter) filters.status = statusFilter;
      const res = await listOrders(filters);
      setOrders(res.orders || []);
      setTotal(res.total || 0);
    } catch (err) {
      setError(err.message || 'Failed to load orders.');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, page]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  useImperativeHandle(ref, () => ({ refresh: fetchOrders }));

  const getActionLabel = (status) => {
    if (status === 'executing') return 'Upload Preview';
    if (status === 'preview_submitted') return 'Review Preview';
    if (status === 'metadata_ready') return 'Upload Final Doc';
    if (status === 'compliance_check' || status === 'pending_verification') return 'Review Final';
    return 'View';
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
      {/* Toolbar */}
      <div className="px-5 py-3 border-b flex items-center justify-between gap-3 flex-wrap">
        <h2 className="font-semibold text-gray-800">Orders</h2>
        <div className="flex items-center gap-2">
          <select
            value={statusFilter}
            onChange={e => { setStatusFilter(e.target.value); setPage(0); }}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
          >
            {FILTER_OPTIONS.map(o => (
              <option key={o.label} value={o.value}>{o.label}</option>
            ))}
          </select>
          <button
            onClick={fetchOrders}
            disabled={loading}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>
      </div>

      {error && (
        <div className="mx-5 mt-3 px-4 py-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>
      )}

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wide">
            <tr>
              <th className="px-4 py-3 text-left">Order ID</th>
              <th className="px-4 py-3 text-left">Client</th>
              <th className="px-4 py-3 text-left">Vendor</th>
              <th className="px-4 py-3 text-left">Service</th>
              <th className="px-4 py-3 text-left">Status</th>
              <th className="px-4 py-3 text-left">Updated</th>
              <th className="px-4 py-3 text-left">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading && orders.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400">Loading…</td></tr>
            ) : orders.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400">No orders found.</td></tr>
            ) : orders.map(order => (
              <tr key={order.id} className="hover:bg-gray-50 transition-colors">
                <td className="px-4 py-3 font-mono text-xs text-gray-700">{order.id}</td>
                <td className="px-4 py-3 text-gray-700">{order.client_name || '—'}</td>
                <td className="px-4 py-3 text-gray-600">{order.vendor_name || '—'}</td>
                <td className="px-4 py-3 text-gray-600 max-w-[140px] truncate">{order.service_name || '—'}</td>
                <td className="px-4 py-3">
                  <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[order.status] || 'bg-gray-100 text-gray-600'}`}>
                    {order.status?.replace(/_/g, ' ')}
                  </span>
                </td>
                <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">
                  {order.updated_at ? formatDateTimeIST(order.updated_at) : '—'}
                </td>
                <td className="px-4 py-3">
                  <button
                    onClick={() => onSelectOrder(order)}
                    className="px-3 py-1 text-xs font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100 transition-colors"
                  >
                    {getActionLabel(order.status)}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="px-5 py-3 border-t flex items-center justify-between text-sm text-gray-600">
          <span>{total} total · page {page + 1} of {totalPages}</span>
          <div className="flex gap-2">
            <button onClick={() => setPage(p => p - 1)} disabled={page === 0} className="px-3 py-1 border rounded-lg hover:bg-gray-50 disabled:opacity-40">← Prev</button>
            <button onClick={() => setPage(p => p + 1)} disabled={page >= totalPages - 1} className="px-3 py-1 border rounded-lg hover:bg-gray-50 disabled:opacity-40">Next →</button>
          </div>
        </div>
      )}
    </div>
  );
});

AdminOrderQueue.displayName = 'AdminOrderQueue';

export default AdminOrderQueue;
